import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { OvertimeManagementService } from '../../services/overtime-management.service';
import { OvertimeDriver } from '../../models/overtime-drivers.model';
import { GetPaginatedOvertimeEntriesRequest } from '../../models/get-paginated-overtime-entries-request.model';
import { Overtime } from '../../models/overtime.model';
import { endOfDay, format, startOfDay, subHours } from 'date-fns';
import { DriverOvertimeTotalCost } from '../../models/driver-overtime-total-cost.model';
import { ConfirmationModalService } from '../../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../../_shared/models/modals/confirmation-options.model';
import { OvertimeStatus } from '../../../../_shared/enums/overtime-status.enum';
import { UpdateOvertimeStatusRequest } from '../../models/update-overtime-status-request.model';
import { FilterService } from '../../../../_shared/services';
import { DateIntervalFilterData } from '../../../../_shared/models';
import { GetDriverOvertimeTotalCostRequest } from '../../models/get-overtime-total-cost-request.model';
import { OvertimeDriverStateService } from '../../services/overtime-driver-state.service';
import { PaginationComponent } from "../../../../_shared/components/pagination/pagination.component";
import { ErrorHandlerService } from '../../../../_shared/services/error-handler.service';
import { CurrencyHelper } from '../../../../_shared/helpers/currency/currency-map.helper';
import { AuthService } from '../../../../_shared/services/auth.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-overtime-driver-details',
  standalone: true,
  imports: [CommonModule, PaginationComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './overtime-driver-details.component.html',
  styleUrl: './overtime-driver-details.component.scss',
})
export class OvertimeDriverDetailsComponent implements OnInit {
  private overtimeManagementService = inject(OvertimeManagementService);
  private overtimeDriverState = inject(OvertimeDriverStateService);
  private confirmationModalService = inject(ConfirmationModalService);
  private filterService = inject(FilterService);
  private errorHandlerService = inject(ErrorHandlerService);
  private auth = inject(AuthService);
  // Declared before the header arrays below, which resolve their labels eagerly.
  private transloco = inject(TranslocoService);

  @Output() loaded = new EventEmitter<boolean>();
  public expandedRowIndex: number | null = null;

  public detailsTableHeaders = [
    { label: this.transloco.translate('overtimeManagement.table.date'), sortable: true, key: 'createdAt' },
    { label: this.transloco.translate('overtimeManagement.table.trips'), sortable: true, key: 'tripCount' },
    { label: this.transloco.translate('overtimeManagement.table.duration'), sortable: true, key: 'signOutTime' },
    { label: this.transloco.translate('overtimeManagement.table.overtime'), sortable: false },
    { label: this.transloco.translate('overtimeManagement.table.cost'), sortable: true, key: 'cost' },
    { label: this.transloco.translate('overtimeManagement.table.action'), sortable: false }
  ];

  public nestedTableHeaders = [
    this.transloco.translate('overtimeManagement.expanded.vehicle'),
    this.transloco.translate('overtimeManagement.expanded.startLocation'),
    this.transloco.translate('overtimeManagement.expanded.endLocation'),
    this.transloco.translate('overtimeManagement.expanded.distanceTraveled'),
    this.transloco.translate('overtimeManagement.expanded.fuelConsumed'),
    this.transloco.translate('overtimeManagement.expanded.drivingTime')
  ];

  selectedDriver: OvertimeDriver | null = null;
  isOvertimeDetailsLoading: boolean = false;
  changePageLoading: boolean = false;
  overtimeStatusLoading: boolean = false;
  overtimeEntries: Overtime[] = [];
  sortedOvertimeEntries: Overtime[] = [];
  sortColumn: string = '';
  sortDirection: string = 'asc';
  currencySymbol: string = '';
  currencyCode: string = '';

  getPaginatedOvertimeEntriesRequest: GetPaginatedOvertimeEntriesRequest = {
    driverId: 0,
    timeFrom: startOfDay(new Date()),
    timeTo: endOfDay(new Date()),
    page: 0,
    pageSize: 0
  };

  getDriverOvertimeTotalCostRequest: GetDriverOvertimeTotalCostRequest = {
    driverId: 0,
    timeFrom: startOfDay(new Date()),
    timeTo: endOfDay(new Date())
  };

  driverOvertimeTotalCost: DriverOvertimeTotalCost;
  noDriverReceived: boolean = false;
  dateFilter: DateIntervalFilterData;

  currentPage: number = 1;
  pageSize: number = 15;
  totalPages: number = 1;
  totalCount: number = 0;
  currentItems: number = 0;

  constructor() {
    this.dateFilter = {} as DateIntervalFilterData;
    this.currencyCode = this.auth.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  ngOnInit(): void {
    this.overtimeDriverState.selectedDriver$.subscribe(driver => {
      if (driver) {
        this.noDriverReceived = false;
        this.selectedDriver = driver;
        this.isOvertimeDetailsLoading = true;
        this.currentPage = 1;
        this.getDriverOvertimeEntries();
        this.getDriverOvertimeTotalCost();
      } else {
        this.noDriverReceived = true;
        this.selectedDriver = null;
      }
    });
  }

  async getDriverOvertimeTotalCost(): Promise<void> {
    if (this.selectedDriver) {
      try {
        this.getDriverOvertimeTotalCostRequest.driverId = this.selectedDriver!.id;
        this.getDriverOvertimeTotalCostRequest.timeFrom = this.dateFilter.startDate!;
        this.getDriverOvertimeTotalCostRequest.timeTo = this.dateFilter.endDate!;
        var totalCostResult = await this.overtimeManagementService.getDriverOvertimeTotalCostById(this.getDriverOvertimeTotalCostRequest);
        if (totalCostResult) {
          if (this.overtimeStatusLoading && totalCostResult.data?.totalCost !== this.driverOvertimeTotalCost.totalCost) {
            this.overtimeDriverState.totalCostUpdated.emit(totalCostResult.data?.totalCost);
          }
          this.driverOvertimeTotalCost = totalCostResult.data!;
        }

        this.loaded.emit(true);
      } catch (error: any) {
        this.errorHandlerService.handleError(error, {
          callback: () => {
            this.loaded.emit(true);
          }
        });
      }
    }
  }

  getDriverOvertimeEntries() {
    this.sortColumn = '';
    this.sortDirection = 'asc';
    this.dateFilter = this.filterService.filter as DateIntervalFilterData;
    setTimeout(async () => {
      if (this.selectedDriver) {
        this.getPaginatedOvertimeEntriesRequest.driverId = this.selectedDriver.id;
        this.getPaginatedOvertimeEntriesRequest.timeFrom = this.dateFilter.startDate!;
        this.getPaginatedOvertimeEntriesRequest.timeTo = this.dateFilter.endDate!;
        this.getPaginatedOvertimeEntriesRequest.page = this.currentPage;
        this.getPaginatedOvertimeEntriesRequest.pageSize = this.pageSize;

        try {
          var result = await this.overtimeManagementService.getPaginatedOvertime(this.getPaginatedOvertimeEntriesRequest);
          if (result.data) {
            this.overtimeEntries = result.data.results;
            this.totalPages = result.data.totalPages;
            this.totalCount = result.data.totalCount;
            this.currentItems = result.data?.results.length ?? 0;
            this.overtimeEntries = this.overtimeEntries.map(row => ({
              ...row,
              duration: this.computeDuration(row.signOutTime, row.overtimeHours),
              formattedHours: this.formatHours(row.overtimeHours)
            }));
            this.sortedOvertimeEntries = [...this.overtimeEntries];
            
            this.isOvertimeDetailsLoading = false;
            this.changePageLoading = false;
            this.overtimeStatusLoading = false;
          }
        } catch (error: any) {
          this.errorHandlerService.handleError(error, {
            callback: () => {
              this.isOvertimeDetailsLoading = false;
              this.changePageLoading = false;
              this.overtimeStatusLoading = false;
            }
          });
        }
      }
    }, 200);
  }

  private computeDuration(signOutTime: Date, overtimeHours: number): string {
    const overtimeStart = subHours(signOutTime, overtimeHours);

    const formatTime = (date: Date) => format(date, "HH:mm");
    return `${formatTime(overtimeStart)} - ${formatTime(signOutTime)}`;
  }

  private formatHours(hours: number): string {
    const hr = Math.floor(hours);
    const min = Math.round((hours - hr) * 60);
    return `${hr}hr${min.toString().padStart(2, '0')}min`;
  }

  formatCost(cost: number): string {
    return `${this.currencySymbol}${cost.toLocaleString('default', { maximumFractionDigits: 2 })}`;
  }

  public toggleRowExpansion(index: number) {
    this.expandedRowIndex = this.expandedRowIndex === index ? null : index;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }
    this.currentPage = page;
    this.changePageLoading = true;
    this.getDriverOvertimeEntries();
  }

  approveOvertime(overtimeId: number) {
    const options: ConfirmationOptions = {
      confirmText: this.transloco.translate('overtimeManagement.approvePrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline'),
      message: this.transloco.translate('overtimeManagement.approvePrompt.message'),
      title: this.transloco.translate('overtimeManagement.approvePrompt.title')
    }
    this.confirmationModalService.showConfirmationModal(options).then(() => {
      this.updateOvertimeStatus(overtimeId, OvertimeStatus.Approved);
    });
  }

  rejectOvertime(overtimeId: number) {
    const options: ConfirmationOptions = {
      confirmText: this.transloco.translate('overtimeManagement.rejectPrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline'),
      message: this.transloco.translate('overtimeManagement.rejectPrompt.message'),
      title: this.transloco.translate('overtimeManagement.rejectPrompt.title')
    }
    this.confirmationModalService.showConfirmationModal(options).then(() => {
      this.updateOvertimeStatus(overtimeId, OvertimeStatus.Rejected);
    });
  }

  revertDecision(overtimeId: number) {
    const options: ConfirmationOptions = {
      confirmText: this.transloco.translate('overtimeManagement.revertPrompt.confirm'),
      declineText: this.transloco.translate('driverInformation.removePrompt.decline'),
      message: this.transloco.translate('overtimeManagement.revertPrompt.message'),
      warningMessage: this.transloco.translate('overtimeManagement.revertPrompt.warning'),
      title: this.transloco.translate('overtimeManagement.revertPrompt.title')
    }
    this.confirmationModalService.showConfirmationModal(options).then(() => {
      this.updateOvertimeStatus(overtimeId, OvertimeStatus.Pending)
    })
  }

  async updateOvertimeStatus(overtimeId: number, newStatus: OvertimeStatus) {
    this.overtimeStatusLoading = true;
    const request: UpdateOvertimeStatusRequest = {
      id: overtimeId,
      overtimeStatus: newStatus
    }
    try {
      const result = await this.overtimeManagementService.updateOvertimeStatusById(request);
      if (!result.errorCode) {
        this.getDriverOvertimeEntries();
        this.getDriverOvertimeTotalCost();
        this.overtimeDriverState.refreshDriverFinanceList();

        this.overtimeStatusLoading = false
      }
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.overtimeStatusLoading = false;
        }
      });
    }
  }

  getCurrentPageDisplay(): string {
    var current = (this.currentPage - 1) * this.pageSize + 1 - Math.min(this.currentPage * this.pageSize, this.totalCount);

    return current.toString();
  }

  sortData(column: string | undefined) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column!;
      this.sortDirection = 'asc';
    }

    this.sortedOvertimeEntries.sort((a, b) => {
      const valueA = a[column as keyof Overtime];
      const valueB = b[column as keyof Overtime];
      if (valueA === valueB) return 0;

      if (this.sortDirection === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
  }
}